import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { buildCurrentPath, useBackNavigation } from '../utils/backNavigation';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Business,
  AccessTime,
  Payment,
  CalendarToday,
  LocationOn,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  EventAvailable,
  FitnessCenter,
  Phone,
  ConfirmationNumber,
  Schedule,
  LocalOffer,
} from '@mui/icons-material';
import { PackPaymentChip, PackBookingTooltip, getPurchasedPackTitle } from '../components/PackBookingTooltip';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { UserNotePanel } from '../components/UserNoteDialog';
import { bookingStudioOf } from '../utils/studioOf';
import { resolveStudioLogoUrl } from '../utils/studioLogo';
import { guestBookingLabel } from '../utils/guestBooking';
import {
  formatDateIST,
  formatDiscountRate,
  getDiscountBreakdown,
  getPaymentStatus,
  getPaymentStatusStyles,
  getSourceLabel,
} from '../utils/bookingPayment';

interface Booking {
  _id: string;
  bookingId?: string;
  user: {
    _id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    image?: string;
  };
  studio: {
    _id: string;
    studioName: string;
    images?: string[];
    address?: string;
    city?: string;
    phoneNumber?: string;
  };
  session?: {
    _id: string;
    className: string;
    image?: string;
    date: string;
    price: number;
    duration?: number;
    description?: string;
  };
  event?: {
    _id: string;
    eventName: string;
    images?: string[];
    date: string;
    price: number;
    description?: string;
  };
  wellnessService?: {
    _id: string;
    name: string;
    duration?: number;
    price?: number;
  };
  wellnessSlotStart?: string;
  wellnessInstructors?: { _id: string; name: string }[];
  assignedInstructor?: { _id: string; name: string };
  bookingType: 'workout' | 'event' | 'trial' | 'wellness';
  bookingStatus: 'BOOKED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW' | 'CHECKED_IN' | string;
  // Guest booking: set when a member booked this seat for `user` from their own pack.
  bookedBy?: any;
  bookedByUser?: { _id?: string; name?: string };
  guestGroupId?: string;
  isPaid?: boolean;
  orderId?: string;
  paymentId?: string;
  isManualBooking?: boolean;
  quantity?: number;
  transaction?: {
    _id: string;
    basePrice: number;
    taxes: number;
    paymentId?: string;
    modeOfPayment?: string;
    isManualBooking?: boolean;
    couponCode?: string;
    coupon?: {
      _id?: string;
      code?: string;
      title?: string;
      discountType?: 'percentage' | 'amount';
      discountValue?: number;
      maxDiscountAmount?: number;
    } | null;
    promoCode?: {
      _id?: string;
      code?: string;
      value?: number;
      valueCap?: number;
      type?: 'percentage' | 'amount';
    } | null;
    purchasedPack?: {
      _id?: string;
      title?: string;
      classesLeft?: number | null;
      totalClasses?: number | null;
      expiryDate?: string;
      packEndDate?: string;
      startDate?: string;
      studio?: { studioName?: string };
      pack?: { title?: string };
    } | null;
  };
  createdAt: string;
  updatedAt: string;
  cancellationReason?: string;
  checkInTime?: string;
  checkOutTime?: string;
}

const BookingDetailPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const handleBack = useBackNavigation('/bookings');
  const [booking, setBooking] = useState<Booking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBookingDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getBookingById(bookingId!);
      setBooking(response.booking);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch booking details');
    } finally {
      setIsLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId, fetchBookingDetails]);

  const getBookingAmount = () => {
    if (!booking) return 0;
    const basePrice = booking.transaction?.basePrice || 0;
    const taxes = booking.transaction?.taxes || 0;
    return basePrice + taxes;
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status.toUpperCase()) {
      case 'BOOKED': return 'success';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      case 'NO_SHOW': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'BOOKED':
      case 'COMPLETED':
        return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'CANCELLED':
        return <Cancel sx={{ fontSize: 16 }} />;
      default:
        return <HourglassEmpty sx={{ fontSize: 16 }} />;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Booking not found'}</Alert>
      </Box>
    );
  }

  const activityName =
    booking.session?.className ||
    booking.event?.eventName ||
    booking.wellnessService?.name ||
    'N/A';
  const activityImage = booking.session?.image || booking.event?.images?.[0];
  const activityDate =
    booking.session?.date || booking.event?.date || booking.wellnessSlotStart;
  const isWorkout = booking.bookingType === 'workout' || booking.bookingType === 'trial';
  const applyDateOffset = !isWorkout && booking.bookingType !== 'wellness';
  const activityDuration = booking.session?.duration ?? booking.wellnessService?.duration;
  const wellnessInstructorNames = [
    ...(booking.wellnessInstructors?.map((i) => i.name).filter(Boolean) ?? []),
    ...(booking.assignedInstructor?.name &&
    !booking.wellnessInstructors?.some((i) => i._id === booking.assignedInstructor?._id)
      ? [booking.assignedInstructor.name]
      : []),
  ];
  const discountBreakdown = getDiscountBreakdown(booking);
  const paymentStatus = getPaymentStatus(booking);

  // Shared UI styles for a clean, consistent look
  const sectionCardSx = { borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
  const pillPaperSx = { p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' };

  return (
    <Box>
      {/* Compact Header */}
      <Box mb={3}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Card sx={{ ...sectionCardSx, p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                src={activityImage}
                variant="rounded"
                sx={{ width: 44, height: 44, borderRadius: 2 }}
              >
                {booking.bookingType === 'workout' ? <FitnessCenter /> : <EventAvailable />}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {activityName}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                  <Chip 
                    label={booking.bookingId || 'N/A'}
                    size="small"
                    icon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
                    sx={{ fontFamily: 'monospace', height: 24 }}
                  />
                  <Box>
                    <Chip
                      label={booking.bookingStatus}
                      size="small"
                      color={getStatusColor(booking.bookingStatus)}
                      icon={getStatusIcon(booking.bookingStatus)}
                      sx={{ fontWeight: 600, height: 24 }}
                    />
                    {(booking.bookingStatus === 'CHECKED_IN' || booking.bookingStatus === 'NO_SHOW') && booking.updatedAt && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.4, fontSize: '0.72rem' }}>
                        {booking.bookingStatus === 'CHECKED_IN' ? 'Checked in' : 'No show'}{' '}
                        {formatDateIST(booking.updatedAt, 'date', false)},{' '}
                        {formatDateIST(booking.updatedAt, 'time', false)}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={booking.bookingType}
                    size="small"
                    icon={booking.bookingType === 'workout' ? <FitnessCenter sx={{ fontSize: 16 }} /> : <EventAvailable sx={{ fontSize: 16 }} />}
                    variant="outlined"
                    sx={{ height: 24, textTransform: 'capitalize' }}
                  />
                  {booking.transaction?.modeOfPayment === 'subscription' && (
                    <PackPaymentChip
                      pack={booking.transaction?.purchasedPack}
                      bookingLocation={booking.studio as any}
                      label="Pack Booking"
                      sx={{ height: 24, fontSize: '0.72rem' }}
                    />
                  )}
                  {(booking.isManualBooking || booking.transaction?.isManualBooking) && (
                    <Chip
                      label="Manual"
                      size="small"
                      sx={{ height: 24, backgroundColor: '#7c3aed', color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
                    />
                  )}
                  {guestBookingLabel(booking) && (
                    <Chip
                      label={guestBookingLabel(booking)}
                      size="small"
                      sx={{ height: 24, backgroundColor: '#00838F20', color: '#00838F', fontWeight: 600, fontSize: '0.72rem' }}
                    />
                  )}
                  {(booking.quantity ?? 1) > 1 && (
                    <Chip
                      label={`${booking.quantity} Tickets`}
                      size="small"
                      color="primary"
                      sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(3, 1fr)' }} gap={1.5}>
              <Paper variant="outlined" sx={pillPaperSx}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {formatDateIST(activityDate, 'date', applyDateOffset)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={pillPaperSx}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Schedule fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">Time</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {formatDateIST(activityDate, 'time', applyDateOffset)}
                </Typography>
              </Paper>
              {activityDuration != null && (
                <Paper variant="outlined" sx={pillPaperSx}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{activityDuration} min</Typography>
                </Paper>
              )}
              {wellnessInstructorNames.length > 0 && (
                <Paper variant="outlined" sx={pillPaperSx}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Instructor</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {wellnessInstructorNames.join(', ')}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Main Content Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column */}
        <Box>
          {/* Activity Overview */}
          <Card sx={{ mb: 3, ...sectionCardSx }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar 
                  src={activityImage}
                  variant="rounded"
                  sx={{ width: 60, height: 60 }}
                >
                  {booking.bookingType === 'workout' ? <FitnessCenter /> : <EventAvailable />}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={700}>
                    {activityName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {booking.bookingType === 'wellness'
                      ? 'Wellness Session'
                      : booking.bookingType === 'workout'
                        ? 'Workout Session'
                        : 'Event'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={pillPaperSx}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDateIST(activityDate, 'date', applyDateOffset)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={pillPaperSx}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Time
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDateIST(activityDate, 'time', applyDateOffset)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {activityDuration != null && (
                  <Paper variant="outlined" sx={pillPaperSx}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTime fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Duration
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {activityDuration} min
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}

                {wellnessInstructorNames.length > 0 && (
                  <Paper variant="outlined" sx={pillPaperSx}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Instructor
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {wellnessInstructorNames.join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* User & Studio Info */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* User Card */}
            <Card sx={sectionCardSx}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    User
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar src={booking.user.image} sx={{ width: 40, height: 40 }}>
                    {booking.user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {booking.user.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.user.email}
                    </Typography>
                  </Box>
                </Box>
                {booking.user.phoneNumber && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="caption">{booking.user.phoneNumber}</Typography>
                  </Box>
                )}
                {booking.user._id && (
                  <UserNotePanel userId={booking.user._id} userName={booking.user.name} studioId={bookingStudioOf(booking)} inline />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() =>
                    navigate(`/users/${booking.user._id}`, {
                      state: { from: buildCurrentPath(location.pathname, location.search) },
                    })
                  }
                  sx={{ mt: 2 }}
                >
                  View Profile
                </Button>
              </CardContent>
            </Card>

            {/* Studio Card */}
            <Card sx={sectionCardSx}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Business fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Studio
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar src={resolveStudioLogoUrl(booking.studio)} variant="rounded" sx={{ width: 40, height: 40 }}>
                    {booking.studio.studioName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {booking.studio.studioName}
                    </Typography>
                    {booking.studio.city && (
                      <Typography variant="caption" color="text.secondary">
                        {booking.studio.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {booking.studio.address && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {booking.studio.address}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() =>
                    navigate(`/studios/${booking.studio._id}`, {
                      state: { from: buildCurrentPath(location.pathname, location.search) },
                    })
                  }
                  sx={{ mt: 2 }}
                >
                  View Studio
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Right Column */}
        <Box>
          {/* Payment Summary */}
          <Card sx={{ mb: 3, ...sectionCardSx }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Payment fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Payment Summary
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Base Price
                    </Typography>
                    {discountBreakdown?.isEstimated && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        (estimated)
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{(discountBreakdown ? discountBreakdown.originalBase : booking.transaction?.basePrice ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                {discountBreakdown && (
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                      <Typography variant="body2" sx={{ color: '#e65100' }}>
                        {`${getSourceLabel(discountBreakdown.source)} Applied`}
                      </Typography>
                      <Box sx={{ mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {discountBreakdown.code && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Code:{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                              {discountBreakdown.code}
                            </Box>
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDiscountRate(discountBreakdown)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: '#e65100', flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      - ₹{discountBreakdown.discountAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Taxes & Fees
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{booking.transaction?.taxes?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={700}>
                    {discountBreakdown ? 'Amount Paid' : 'Total'}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    ₹{getBookingAmount().toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>

              <Paper
                variant="outlined"
                sx={() => {
                  const s = getPaymentStatusStyles(paymentStatus);
                  return { p: 2, bgcolor: s.bg, borderColor: s.border };
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  {paymentStatus === 'Pack' && booking.transaction?.purchasedPack ? (
                    <PackBookingTooltip
                      pack={booking.transaction.purchasedPack}
                      bookingLocation={booking.studio as any}
                    >
                      <Box component="span" display="inline-flex">
                        <Chip
                          label={paymentStatus}
                          size="small"
                          sx={() => {
                            const s = getPaymentStatusStyles(paymentStatus);
                            return { fontWeight: 700, height: 24, bgcolor: s.bg, color: s.color, border: '1px solid ' + s.border };
                          }}
                        />
                      </Box>
                    </PackBookingTooltip>
                  ) : (
                    <Chip
                      label={paymentStatus}
                      size="small"
                      sx={() => {
                        const s = getPaymentStatusStyles(paymentStatus);
                        return { fontWeight: 700, height: 24, bgcolor: s.bg, color: s.color, border: '1px solid ' + s.border };
                      }}
                    />
                  )}
                </Box>

                {booking.transaction?.modeOfPayment === 'subscription' && booking.transaction?.purchasedPack && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Pack used
                    </Typography>
                    <PackPaymentChip
                      pack={booking.transaction.purchasedPack}
                      bookingLocation={booking.studio as any}
                      label={getPurchasedPackTitle(booking.transaction.purchasedPack)}
                      sx={{ height: 24, fontSize: '0.72rem', maxWidth: '100%' }}
                    />
                  </Box>
                )}

                {discountBreakdown && (
                  <Box mb={1.5} display="flex" alignItems="center" gap={0.5}>
                    <LocalOffer sx={{ fontSize: 16, color: '#0d9488' }} />
                    <Typography variant="caption" sx={{ color: '#0d9488', fontWeight: 600 }}>
                      {`${getSourceLabel(discountBreakdown.source)} Applied: ${discountBreakdown.code ?? 'N/A'}`}
                    </Typography>
                  </Box>
                )}

                {booking.transaction?.paymentId && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Payment ID
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {booking.transaction.paymentId}
                    </Typography>
                  </Box>
                )}

                {booking.orderId && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Order ID
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {booking.orderId}
                    </Typography>
                  </Box>
                )}

                {booking.transaction?.modeOfPayment && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Method
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                      {discountBreakdown && booking.transaction.modeOfPayment === 'free'
                        ? `${getSourceLabel(discountBreakdown.source)} Redemption`
                        : (
                          <>
                            {booking.transaction.modeOfPayment}
                            {discountBreakdown && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ textTransform: 'none' }}>
                                {` · ${getSourceLabel(discountBreakdown.source)} Applied`}
                              </Typography>
                            )}
                          </>
                        )}
                    </Typography>
                  </Box>
                )}

              </Paper>
            </CardContent>
          </Card>

          {/* Booking Timeline */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Timeline
                </Typography>
              </Box>

              <Box sx={{ position: 'relative', pl: 3 }}>
                {/* Timeline Line */}
                <Box sx={{ position: 'absolute', left: 7, top: 16, bottom: 16, width: 2, bgcolor: 'divider', borderRadius: 1 }} />
                
                {/* Created */}
                <Box sx={{ position: 'relative', mb: 2, pl: 2 }}>
                  <Box sx={{ position: 'absolute', left: -18, top: -2, width: 16, height: 16, borderRadius: '50%', bgcolor: colors.primary, border: '2px solid white', boxShadow: '0 0 0 2px' + colors.primary, zIndex: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    Created
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatDateIST(booking.createdAt, 'date', false)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatDateIST(booking.createdAt, 'time', false)}
                  </Typography>
                </Box>

                {/* Connecting Line */}
                <Box sx={{ mb: 1.5, pl: 4 }}>
                  <Box sx={{ height: 24, borderLeft: '2px dashed', borderColor: 'divider' }} />
                </Box>

                {/* Paid */}
                {booking.transaction?.paymentId && (
                  <>
                    <Box sx={{ position: 'relative', mb: 2, pl: 2 }}>
                      <Box sx={{ position: 'absolute', left: -18, top: -2, width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50', border: '2px solid white', boxShadow: '0 0 0 2px #4caf50', zIndex: 1 }} />
                      <Typography variant="body2" fontWeight={600}>
                        Paid
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ₹{getBookingAmount().toLocaleString()}
                      </Typography>
                    </Box>
                    
                    {/* Connecting Line */}
                    <Box sx={{ mb: 1.5, pl: 4 }}>
                      <Box sx={{ height: 24, borderLeft: '2px dashed', borderColor: 'divider' }} />
                    </Box>
                  </>
                )}

                {/* Status */}
                {booking.updatedAt !== booking.createdAt && (
                  <Box sx={{ position: 'relative', pl: 2 }}>
                    <Box sx={{
                      position: 'absolute', left: -18, top: -2, width: 16, height: 16, borderRadius: '50%',
                      bgcolor: booking.bookingStatus === 'CHECKED_IN' ? '#4caf50'
                        : booking.bookingStatus === 'NO_SHOW' ? '#ff5722'
                        : booking.bookingStatus === 'CANCELLED' ? '#f44336'
                        : '#ff9800',
                      border: '2px solid white',
                      boxShadow: '0 0 0 2px ' + (
                        booking.bookingStatus === 'CHECKED_IN' ? '#4caf50'
                        : booking.bookingStatus === 'NO_SHOW' ? '#ff5722'
                        : booking.bookingStatus === 'CANCELLED' ? '#f44336'
                        : '#ff9800'
                      ),
                      zIndex: 1,
                    }} />
                    <Typography variant="body2" fontWeight={600}>
                      {booking.bookingStatus === 'CHECKED_IN' ? 'Checked In'
                        : booking.bookingStatus === 'NO_SHOW' ? 'No Show'
                        : booking.bookingStatus === 'CANCELLED' ? 'Cancelled'
                        : booking.bookingStatus}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDateIST(booking.updatedAt, 'date', false)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDateIST(booking.updatedAt, 'time', false)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default BookingDetailPage;
